import React, { useState } from "react";
import { Box, IconButton, Popover, Tooltip, Typography } from "@material-ui/core";
import BorderAllIcon from "@material-ui/icons/BorderAll";

const GRID_ROWS = 10;
const GRID_COLS = 10;
const CELL_SIZE = 18;

/**
 * Toolbar item for `table`: a Word-style size picker. Hover the grid to pick
 * rows x columns, click to insert. Row/column editing lives in the context
 * strip (TableContextStrip), which appears once the caret is inside a table.
 */
export default function TableGridPicker({ editor }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [size, setSize] = useState({ rows: 0, cols: 0 });
  const inTable = editor.isActive("table");

  const close = () => {
    setAnchorEl(null);
    setSize({ rows: 0, cols: 0 });
  };

  const insert = (rows, cols) => {
    editor
      .chain()
      .focus()
      .insertTable({ rows, cols, withHeaderRow: true })
      // border="1" is TinyMCE's default for a new table, and what the editor
      // CSS and bac/html_docx.py key cell borders off - without it the table
      // is invisible on screen and borderless in the .docx.
      .updateAttributes("table", { border: "1", style: "width: 100%" })
      .run();
    close();
  };

  return (
    <>
      <Tooltip title="Insert table (or type +---+---+ and press Enter)">
        <span>
          <IconButton
            size="small"
            color={inTable ? "primary" : "default"}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            <BorderAllIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          close();
          editor.commands.focus();
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        // MUI would otherwise hand focus back to the toolbar button as the
        // popover closes - after insert() has focused the editor - and the
        // caret would vanish from the new table.
        disableRestoreFocus
      >
        <Box p={1}>
          <div
            className="rte__table-grid"
            style={{
              gridTemplateColumns: `repeat(${GRID_COLS}, ${CELL_SIZE}px)`,
              gridAutoRows: `${CELL_SIZE}px`,
            }}
            onMouseLeave={() => setSize({ rows: 0, cols: 0 })}
          >
            {Array.from({ length: GRID_ROWS * GRID_COLS }, (_, index) => {
              const row = Math.floor(index / GRID_COLS) + 1;
              const col = (index % GRID_COLS) + 1;
              const lit = row <= size.rows && col <= size.cols;
              return (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                <div
                  key={index}
                  className={`rte__table-grid-cell${lit ? " is-lit" : ""}`}
                  onMouseEnter={() => setSize({ rows: row, cols: col })}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => insert(row, col)}
                />
              );
            })}
          </div>
          <Typography
            variant="caption"
            component="div"
            align="center"
            className="rte__table-grid-label"
          >
            {size.rows
              ? `${size.rows} × ${size.cols}`
              : "Rows × columns"}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
